'use client';

import { useState, useCallback, useRef } from 'react';
import type { LogosContextForKaira } from '../../lib/kaira/logos-integration/types';
import { getApiBaseUrl } from '../../utils/api-base';

const API_BASE = getApiBaseUrl();

export type KairaMode = 'checkin' | 'therapy';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function useKairaLogos(sessionId?: string, language: 'en' | 'es' = 'es', userId?: number | string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<KairaMode>('checkin');
  const [extractionReady, setExtractionReady] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const logosContextRef = useRef<LogosContextForKaira | null>(null);

  const setLogosContext = useCallback((ctx: LogosContextForKaira) => {
    logosContextRef.current = ctx;
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = { role: 'user', content: content.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/kaira/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedMessages,
          sessionId,
          mode,
          userId,
          logosContext: logosContextRef.current,
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(language === 'es'
          ? 'Hubo un error. Por favor intenta de nuevo.'
          : 'There was an error. Please try again.');
        setMessages(prev => prev.slice(0, -1));
        return;
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);

      // Check if extraction is ready (check-in mode)
      if (data.extractionReady && mode === 'checkin') {
        setExtractionReady(true);
      }

    } catch {
      setError(language === 'es'
        ? 'Error de conexión. Verifica tu internet.'
        : 'Connection error. Check your internet.');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading, sessionId, mode, language]);

  const extractDimensions = useCallback(async (existingState?: Record<string, number>) => {
    if (!extractionReady || isExtracting) return null;
    setIsExtracting(true);

    try {
      const res = await fetch(`${API_BASE}/api/kaira/logos-checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, sessionId, existingState }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error('Extraction failed');

      return data;
    } catch (err) {
      console.error('Dimension extraction error:', err);
      return null;
    } finally {
      setIsExtracting(false);
    }
  }, [extractionReady, isExtracting, messages, sessionId]);

  const switchToTherapy = useCallback((logosCtx: LogosContextForKaira) => {
    logosContextRef.current = logosCtx;
    setMode('therapy');
    setExtractionReady(false);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setExtractionReady(false);
    setMode('checkin');
  }, []);

  return {
    messages,
    isLoading,
    error,
    mode,
    extractionReady,
    isExtracting,
    sendMessage,
    extractDimensions,
    switchToTherapy,
    setLogosContext,
    clearMessages,
  };
}
